/** @format */
import { RefObject, useEffect, useRef, useState } from 'react';

/**
 * @deprecated use @feather/hooks/use-buffered-mesurements
 */
export type BufferedMeasurement = {
    width: number;
    height: number;
};

/**
 * @deprecated use @feather/hooks/use-buffered-mesurements
 */
export type BufferedMeasurements<T extends object> = {
    [key in keyof T]: BufferedMeasurement;
};

type BufferedMeasurementRefs<T extends object> = {
    [key in keyof T]: RefObject<any>;
};

/**
 * @deprecated use @feather/hooks/use-buffered-mesurements
 */
export enum BufferedMeasurementsStatus {
    CLEARING = 'clearing',
    MEASURING = 'measuring',
    FINISHED = 'finished',
}

/**
 * @deprecated use @feather/hooks/use-buffered-mesurements
 */
export type UseBufferedMeasurementsProps<T extends object> = {
    refs: BufferedMeasurementRefs<T>;
};

/**
 * @deprecated use @feather/hooks/use-buffered-mesurements
 */
export const useBufferedMeasurements = <T extends object>({
    refs,
}: UseBufferedMeasurementsProps<T>) => {
    const refKeys = Object.keys(refs);
    const defaultMeasurements = refKeys.reduce(
        (measurements, refKey) => ({
            ...measurements,
            [refKey]: { width: 0, height: 0 },
        }),
        {} as BufferedMeasurements<T>
    );

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [status, setStatus] = useState<BufferedMeasurementsStatus>(
        BufferedMeasurementsStatus.CLEARING
    );

    const [measurements, setMeasurements] = useState(defaultMeasurements);

    const setStatusNextRender = (newStatus: BufferedMeasurementsStatus) => {
        timerRef.current = setTimeout(() => setStatus(newStatus), 0);
    };

    const clearScheduledStatusUpdates = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    };

    const remeasure = () => {
        if (status === BufferedMeasurementsStatus.FINISHED) {
            setStatus(BufferedMeasurementsStatus.CLEARING);
        }
    };

    const clearMeasurements = () => {
        setMeasurements(defaultMeasurements);
        setStatusNextRender(BufferedMeasurementsStatus.MEASURING);
    };

    const getMeasurements = () => {
        const newMeasurements = {} as BufferedMeasurements<T>;

        for (const [key, value] of Object.entries<RefObject<any>>(refs)) {
            const { clientWidth, clientHeight } = value?.current || {};
            // @ts-ignore - Element implicitly has an any type - Touch and Change
            newMeasurements[key] = { width: clientWidth, height: clientHeight };
        }

        setMeasurements(newMeasurements);
        setStatusNextRender(BufferedMeasurementsStatus.FINISHED);
    };

    useEffect(() => {
        switch (status) {
            case BufferedMeasurementsStatus.CLEARING:
                clearMeasurements();
                break;
            case BufferedMeasurementsStatus.MEASURING:
                getMeasurements();
                break;
        }
    }, [status]);

    useEffect(() => {
        return () => clearScheduledStatusUpdates();
    }, []);

    return { status, measurements, remeasure };
};
